import { AccountBalance, CheckCircle, ErrorOutline, MoneyOff, Paid, RequestQuote } from "@mui/icons-material";
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { FinancialService } from "../../../services/FinancialService";
import { FinancialSummaryDTO } from "../../../types/FinancialDTO";
import { formatCurrency, monthLabel } from "../../../utils/financialFunctions";
import { useAlert } from "../../contextProviders/AlertProvider";
import EpiStatCard from "../../epi/EpiStatCard";
import { EPI_COLORS } from "../../epi/epiColors";
import "./FinancialIndicators.scss";

const FinancialIndicators: FC = () => {
	const [months, setMonths] = useState<string[]>([]);
	const [month, setMonth] = useState('');
	const [summary, setSummary] = useState<FinancialSummaryDTO | null>(null);
	const { showError } = useAlert();
	const service = new FinancialService();

	useEffect(() => {
		const loadMonths = async () => {
			const response = await service.getMonths();
			const available = response?.data || [];
			setMonths(available);
			setMonth(available[0] || '');
		};
		loadMonths();
	}, []);

	useEffect(() => {
		if (!month) {
			return;
		}
		let cancelled = false;
		const loadSummary = async () => {
			const response = await service.getSummary(month);
			if (cancelled) {
				return;
			}
			if (!response) {
				showError('Não foi possível carregar os indicadores. Recarregue a página para tentar novamente.');
			}
			setSummary(response?.data || null);
		};
		loadSummary();
		return () => {
			cancelled = true;
		};
	}, [month]);

	const count = (value?: number) => value === undefined ? '—' : String(value);

	return (
		<div className="financial-indicators">
			<FormControl size="small" className="financial-indicators-filter">
				<InputLabel id="financial-indicators-month">Mês dos indicadores</InputLabel>
				<Select labelId="financial-indicators-month" label="Mês dos indicadores" value={month} onChange={(e) => setMonth(e.target.value)}>
					{months.map(item => <MenuItem key={item} value={item}>{monthLabel(item)}</MenuItem>)}
				</Select>
				<FormHelperText>Aplica-se somente aos indicadores ao lado</FormHelperText>
			</FormControl>
			<div className="financial-indicators-cards">
				<EpiStatCard variant="label-first" icon={<RequestQuote />} color={EPI_COLORS.info} label="Empresas faturadas"
					value={count(summary?.companies)} />
				<EpiStatCard variant="label-first" icon={<CheckCircle />} color={EPI_COLORS.success} label="Empresas em dia"
					value={count(summary?.companiesUpToDate)} />
				<EpiStatCard variant="label-first" icon={<ErrorOutline />} color={EPI_COLORS.danger} label="Empresas com faturamento vencido"
					value={count(summary?.companiesOverdue)} />
				<EpiStatCard variant="label-first" icon={<AccountBalance />} color={EPI_COLORS.purple} label="Faturamento total"
					value={summary ? formatCurrency(summary.total) : '—'} />
				<EpiStatCard variant="label-first" icon={<Paid />} color={EPI_COLORS.ok} label="Faturamento pago"
					value={summary ? formatCurrency(summary.paid) : '—'} />
				<EpiStatCard variant="label-first" icon={<MoneyOff />} color={EPI_COLORS.warning} label="Faturamento não pago"
					value={summary ? formatCurrency(summary.unpaid) : '—'} />
			</div>
		</div>
	);
}

export default FinancialIndicators
